import { useMutation, type QueryClient } from '@tanstack/react-query';

import { type Audit } from '#packages/data-collection/models/audit';
import { type AuditBackgroundForm } from '#packages/data-collection/models/audit-background-form';
import { type AuditMechanicalForm } from '#packages/data-collection/models/audit-mechanical-form';
import { type AuditLightingTypeForm } from '#packages/data-collection/models/audit-lighting-type-form';
import { type AuditLightingCountForm } from '#packages/data-collection/models/audit-lighting-count-form';
import { type AuditWaterFixtureForm } from '#packages/data-collection/models/audit-water-fixture-form';
import { type AuditChecklistForm } from '#packages/data-collection/models/audit-checklist-form';
import { type AuditWindowsForm } from '#packages/data-collection/models/audit-windows-form';
import { type AuditEcmsForm } from '#packages/data-collection/models/audit-ecms-form';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { type ExportApiService } from '#apps/client-web/features/export/services/export-api';
import { populateWorkbook, populateTable } from '#apps/client-web/features/export/services/excel-workbook-populator';
import { backgroundSheetMapping } from '#apps/client-web/features/export/mappings/background';
import { mechanicalTableMapping } from '#apps/client-web/features/export/mappings/mechanical';
import { lightingTypeTableMapping } from '#apps/client-web/features/export/mappings/lighting-type';
import { lightingCountTableMapping } from '#apps/client-web/features/export/mappings/lighting-count';
import { waterFixtureTableMapping } from '#apps/client-web/features/export/mappings/water-fixture';
import { checklistSheetMapping } from '#apps/client-web/features/export/mappings/checklist';
import { windowsTableMapping } from '#apps/client-web/features/export/mappings/windows';
import { ecmsSheetMapping } from '#apps/client-web/features/export/mappings/ecms';

const EXPORT_TEMPLATE_QUERY_KEY = 'export-template';

const TEMPLATE_ID = 'dcf_ea_template.xlsx';

export type ExportProgress = {
    percent: number;
    label: string;
};

export type ExportProgressCallback = (progress: ExportProgress) => void;

/**
 * Ordered list of export stages with their relative weights.
 * Percentages are computed as cumulative weight / total weight.
 * Weights roughly reflect the time each stage takes (network-bound stages weigh more).
 */
const EXPORT_STAGES = [
    { key: 'request-url', label: 'Requesting template…', weight: 5 },
    { key: 'download-template', label: 'Downloading template…', weight: 15 },
    { key: 'load-workbook', label: 'Loading workbook…', weight: 10 },
    { key: 'populate-background', label: 'Populating Background…', weight: 6 },
    { key: 'populate-mechanical', label: 'Populating Mechanical…', weight: 6 },
    { key: 'populate-lighting-type', label: 'Populating Lighting Types…', weight: 6 },
    { key: 'populate-lighting-count', label: 'Populating Lighting Counts…', weight: 6 },
    { key: 'populate-water-fixture', label: 'Populating Water Fixtures…', weight: 6 },
    { key: 'populate-checklist', label: 'Populating Checklist…', weight: 6 },
    { key: 'populate-windows', label: 'Populating Windows…', weight: 6 },
    { key: 'populate-ecms', label: 'Populating ECMs…', weight: 6 },
    { key: 'write-buffer', label: 'Generating file…', weight: 18 },
    { key: 'download-complete', label: 'Download ready', weight: 4 },
] as const;

type ExportStageKey = (typeof EXPORT_STAGES)[number]['key'];

const TOTAL_WEIGHT = EXPORT_STAGES.reduce((sum, s) => sum + s.weight, 0);

const makeProgressReporter = (onProgress: ExportProgressCallback | undefined) => {
    let cumulative = 0;
    return (stageKey: ExportStageKey) => {
        const stage = EXPORT_STAGES.find((s) => s.key === stageKey);
        if (!stage) return;
        cumulative += stage.weight;
        const percent = Math.min(100, Math.round((cumulative / TOTAL_WEIGHT) * 100));
        onProgress?.({ percent, label: stage.label });
    };
};

type UseExportTemplateMutationProps = LoggingContext & {
    exportApiService: ExportApiService;
    auditFormService: AuditFormService;
    queryClient: QueryClient;
    getAccessToken: () => string | undefined;
};

export const useExportTemplateMutation = ({
    exportApiService,
    auditFormService,
    queryClient,
    getAccessToken,
    logger,
}: UseExportTemplateMutationProps) => {
    return useMutation(
        {
            mutationKey: [EXPORT_TEMPLATE_QUERY_KEY],
            mutationFn: async ({
                audit,
                onProgress,
            }: {
                audit: Audit;
                onProgress?: ExportProgressCallback;
            }) => {
                const reportStage = makeProgressReporter(onProgress);
                const accessToken = getAccessToken();

                /* 1. Get the presigned download URL for the template */
                logger?.debug('Requesting template download URL');

                const downloadUrl =
                    await exportApiService.getTemplateDownloadUrl({
                        templateId: TEMPLATE_ID,
                        accessToken,
                    });

                reportStage('request-url');
                logger?.debug(
                    { downloadUrl },
                    'Template download URL received',
                );

                /* 2. Fetch the template as binary */
                const templateResponse = await fetch(downloadUrl);

                if (!templateResponse.ok) {
                    throw new Error(
                        `Failed to fetch template: ${templateResponse.status} ${templateResponse.statusText}`,
                    );
                }

                const templateBuffer = await templateResponse.arrayBuffer();
                reportStage('download-template');

                /* 3. Load the workbook with ExcelJS (lazy-loaded to keep main bundle small) */
                const { Workbook } = await import('exceljs');
                const workbook = new Workbook();
                await workbook.xlsx.load(templateBuffer);

                reportStage('load-workbook');
                logger?.debug('Template workbook loaded');

                /* 4. Load form data and populate */
                const backgroundForm = await auditFormService.loadByType({
                    auditId: audit.id,
                    formType: 'background',
                });

                if (backgroundForm && backgroundForm.type === 'background') {
                    const result = populateWorkbook({
                        workbook,
                        mapping: backgroundSheetMapping,
                        data: {
                            audit,
                            form: backgroundForm as AuditBackgroundForm,
                        },
                    });

                    logger?.debug(
                        {
                            populated: result.populated,
                            skippedCount: result.skipped.length,
                            skipped: result.skipped,
                        },
                        'Background sheet populated',
                    );
                } else {
                    logger?.debug(
                        'No background form found — exporting template without background data',
                    );
                }
                reportStage('populate-background');

                const mechanicalForm = await auditFormService.loadByType({
                    auditId: audit.id,
                    formType: 'mechanical',
                });

                if (mechanicalForm && mechanicalForm.type === 'mechanical') {
                    const mechForm = mechanicalForm as AuditMechanicalForm;
                    const result = populateTable({
                        workbook,
                        mapping: mechanicalTableMapping,
                        entries: mechForm.entries,
                    });

                    logger?.debug(
                        {
                            populated: result.populated,
                            skippedCount: result.skipped.length,
                            skipped: result.skipped,
                        },
                        'Mechanical sheet populated',
                    );
                } else {
                    logger?.debug(
                        'No mechanical form found — exporting template without mechanical data',
                    );
                }
                reportStage('populate-mechanical');

                const lightingTypeForm = await auditFormService.loadByType({
                    auditId: audit.id,
                    formType: 'lighting-type',
                });

                if (lightingTypeForm && lightingTypeForm.type === 'lighting-type') {
                    const ltForm = lightingTypeForm as AuditLightingTypeForm;
                    const result = populateTable({
                        workbook,
                        mapping: lightingTypeTableMapping,
                        entries: ltForm.entries,
                    });

                    logger?.debug(
                        {
                            populated: result.populated,
                            skippedCount: result.skipped.length,
                            skipped: result.skipped,
                        },
                        'Lighting types sheet populated',
                    );

                    // Lighting count depends on lighting type entries for fixture type resolution
                    const lightingCountForm = await auditFormService.loadByType({
                        auditId: audit.id,
                        formType: 'lighting-count',
                    });

                    if (lightingCountForm && lightingCountForm.type === 'lighting-count') {
                        const lcForm = lightingCountForm as AuditLightingCountForm;
                        const lcResult = populateTable({
                            workbook,
                            mapping: lightingCountTableMapping,
                            entries: lcForm.entries.map((entry) => ({
                                entry,
                                lightingTypeEntries: ltForm.entries,
                            })),
                        });

                        logger?.debug(
                            {
                                populated: lcResult.populated,
                                skippedCount: lcResult.skipped.length,
                                skipped: lcResult.skipped,
                            },
                            'Lighting count sheet populated',
                        );
                    } else {
                        logger?.debug(
                            'No lighting count form found — exporting template without lighting count data',
                        );
                    }
                } else {
                    logger?.debug(
                        'No lighting type form found — exporting template without lighting type data',
                    );
                }
                reportStage('populate-lighting-type');
                reportStage('populate-lighting-count');

                const waterFixtureForm = await auditFormService.loadByType({
                    auditId: audit.id,
                    formType: 'water-fixture',
                });

                if (waterFixtureForm && waterFixtureForm.type === 'water-fixture') {
                    const wfForm = waterFixtureForm as AuditWaterFixtureForm;
                    const result = populateTable({
                        workbook,
                        mapping: waterFixtureTableMapping,
                        entries: wfForm.entries,
                    });

                    logger?.debug(
                        {
                            populated: result.populated,
                            skippedCount: result.skipped.length,
                            skipped: result.skipped,
                        },
                        'Water fixture sheet populated',
                    );
                } else {
                    logger?.debug(
                        'No water fixture form found — exporting template without water fixture data',
                    );
                }
                reportStage('populate-water-fixture');

                const checklistForm = await auditFormService.loadByType({
                    auditId: audit.id,
                    formType: 'checklist',
                });

                if (checklistForm && checklistForm.type === 'checklist') {
                    const result = populateWorkbook({
                        workbook,
                        mapping: checklistSheetMapping,
                        data: checklistForm as AuditChecklistForm,
                    });

                    logger?.debug(
                        {
                            populated: result.populated,
                            skippedCount: result.skipped.length,
                            skipped: result.skipped,
                        },
                        'Checklist sheet populated',
                    );
                } else {
                    logger?.debug(
                        'No checklist form found — exporting template without checklist data',
                    );
                }
                reportStage('populate-checklist');

                const windowsForm = await auditFormService.loadByType({
                    auditId: audit.id,
                    formType: 'windows',
                });

                if (windowsForm && windowsForm.type === 'windows') {
                    const wForm = windowsForm as AuditWindowsForm;
                    const result = populateTable({
                        workbook,
                        mapping: windowsTableMapping,
                        entries: wForm.entries,
                    });

                    logger?.debug(
                        {
                            populated: result.populated,
                            skippedCount: result.skipped.length,
                            skipped: result.skipped,
                        },
                        'Windows sheet populated',
                    );
                } else {
                    logger?.debug(
                        'No windows form found — exporting template without windows data',
                    );
                }
                reportStage('populate-windows');

                const ecmsForm = await auditFormService.loadByType({
                    auditId: audit.id,
                    formType: 'ecms',
                });

                if (ecmsForm && ecmsForm.type === 'ecms') {
                    const result = populateWorkbook({
                        workbook,
                        mapping: ecmsSheetMapping,
                        data: ecmsForm as AuditEcmsForm,
                    });

                    logger?.debug(
                        {
                            populated: result.populated,
                            skippedCount: result.skipped.length,
                            skipped: result.skipped,
                        },
                        'ECMs sheet populated',
                    );
                } else {
                    logger?.debug(
                        'No ECMs form found — exporting template without ECMs data',
                    );
                }
                reportStage('populate-ecms');

                /* 5. Generate the populated file and trigger download */
                const outputBuffer = await workbook.xlsx.writeBuffer();
                reportStage('write-buffer');
                const blob = new Blob([outputBuffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });

                const fileName = `${audit.title.replace(/[^a-zA-Z0-9_-]/g, '_')}_export.xlsx`;

                const url = URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = fileName;
                anchor.style.display = 'none';
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
                URL.revokeObjectURL(url);

                reportStage('download-complete');
                logger?.debug({ fileName }, 'Export download triggered');
            },
        },
        queryClient,
    );
};
